import { useSyncExternalStore } from 'react';

import { areaForPrincipal, type Area } from './areas';
import {
  getSessionState,
  subscribeToSession,
  type Session,
  type SessionState,
} from './sessionStore';

/**
 * Reading the session.
 *
 * The store is a plain module rather than a React context on purpose: the API
 * layer needs the token from outside the component tree, and one source of
 * truth that both can read beats a context plus a mirror of it that can drift.
 * `useSyncExternalStore` is what keeps React's view of it correct under
 * concurrent rendering.
 */
export function useSessionState(): SessionState {
  return useSyncExternalStore(subscribeToSession, getSessionState, getSessionState);
}

export function useSession(): Session | null {
  return useSessionState().session;
}

/**
 * The session, for code that only ever runs behind a route guard.
 *
 * Throwing here is the right failure: it means a screen was mounted outside the
 * guard, which is a routing bug, and a loud one is better than a screen that
 * renders `undefined` where a person's name should be.
 */
export function useRequiredSession(): Session {
  const session = useSession();
  if (session === null) {
    throw new Error(
      'useRequiredSession foi chamado fora de uma rota autenticada. ' +
        'Envolva a rota em <RequireSession>.',
    );
  }
  return session;
}

/** Which half of the product the signed-in principal belongs to. */
export function useCurrentArea(): Area | null {
  const session = useSession();
  return session === null ? null : areaForPrincipal(session.principal);
}
